import { IRefundOffer } from "../../Orchestrator/Types/Types/BookingTypes";

/** The wire shape of an OSDM amount: minor units plus the scale to divide by. */
export interface IRefundMoney {
  currency: string;
  /** Minor units, e.g. 6000 for AED 60.00 at scale 2. */
  amount: number;
  scale: number;
}

/** Both halves of a refund quote, in one currency and one scale. */
export interface IRefundTotals {
  refundFee: IRefundMoney;
  refundableAmount: IRefundMoney;
}

const isMoney = (value: any): value is IRefundMoney =>
  !!value && typeof value.amount === "number" && Number.isFinite(value.amount);

/**
 * Add up every refund offer the retailer quoted.
 *
 * `POST /bookings/{id}/refund-offers` answers with an ARRAY. It carries more
 * than one element whenever the request covers more than one booked offer, and
 * each element has its OWN `refundFee` and `refundableAmount` (see
 * `Turnit.Osdm.RefundOfferCollectionResponse` in docs/retailer.json, and
 * er-transcity `apps/@sds/docs/assets/api-resources/refund.md`). Reading
 * `refundOffers[0]` — which the cancel page did — quotes the first offer and
 * silently drops the rest: on a two-passenger booking the passenger is shown one
 * passenger's fee and one passenger's refund.
 *
 * Every other consumer already iterates the whole array (beef's
 * `payment_refund.go`, `TurnitShopController`, `onwardRefund.sumRefundOffers`);
 * this is that rule, shared.
 *
 * RETURNS NULL RATHER THAN A NUMBER whenever the total cannot be established:
 *
 *  - no offers at all — the retailer declined to quote;
 *  - an offer missing `refundFee` or `refundableAmount`;
 *  - offers disagreeing about `currency` or `scale`, which cannot be added
 *    without inventing a conversion.
 *
 * This is money code: a null must reach the user as an error. It must NOT be
 * coerced to 0 — "we could not price this" and "this costs nothing" are
 * different answers, and only one of them is safe to charge against.
 *
 * A present `{amount: 0}` IS a real answer and sums to zero, which is the whole
 * point when a product's refund fee has been removed from configuration.
 */
export const sumRefundOffers = (
  offers: IRefundOffer[] | undefined | null
): IRefundTotals | null => {
  if (!offers || offers.length === 0) return null;

  let currency = "";
  let scale: number | null = null;
  let feeAmount = 0;
  let refundableAmount = 0;

  for (const offer of offers) {
    const fee = offer?.refundFee as unknown as IRefundMoney | undefined;
    const refundable = offer?.refundableAmount as unknown as IRefundMoney | undefined;
    if (!isMoney(fee) || !isMoney(refundable)) return null;

    for (const money of [fee, refundable]) {
      // First one seen sets the currency/scale; every later one must match it.
      // `scale` is compared against null, not falsiness — scale 0 is legal.
      if (scale === null) {
        scale = money.scale;
        currency = money.currency || "";
      } else if (money.scale !== scale || (money.currency || "") !== currency) {
        return null;
      }
    }

    feeAmount += fee.amount;
    refundableAmount += refundable.amount;
  }

  if (scale === null) return null;
  return {
    refundFee: { currency, scale, amount: feeAmount },
    refundableAmount: { currency, scale, amount: refundableAmount },
  };
};

/**
 * Add two totals that were quoted by SEPARATE refund-offers calls.
 *
 * A round trip needs one call per direction — the retailer rejects a single
 * request whose `fulfillmentIds` span two legs (er-transcity
 * `handlers/payment_structs.go`, `RefundLeg`) — so the page's headline figures
 * are the sum of several quotes.
 *
 * Same fail-loud contract as [[sumRefundOffers]]: a null operand, or operands in
 * different currencies/scales, gives null.
 */
export const addRefundTotals = (
  left: IRefundTotals | null,
  right: IRefundTotals | null
): IRefundTotals | null => {
  if (!left) return right;
  if (!right) return left;
  if (
    left.refundFee.scale !== right.refundFee.scale ||
    left.refundFee.currency !== right.refundFee.currency
  ) {
    return null;
  }
  const { currency, scale } = left.refundFee;
  return {
    refundFee: { currency, scale, amount: left.refundFee.amount + right.refundFee.amount },
    refundableAmount: {
      currency,
      scale,
      amount: left.refundableAmount.amount + right.refundableAmount.amount,
    },
  };
};
